import React, { useState } from 'react';

import './cards-filter.scss';
import { yupDateTransformer } from 'utilities/dayjs-utilities';
import {
  Form,
  Button as AmpolButton,
  ButtonVariant,
  ButtonColor,
  ButtonType,
  FormValidationMode,
  FormField,
  ReadonlyField,
} from '@ampol/design-system';
import { useSelector } from 'react-redux';
import { cardStatusFilterOptions } from 'utilities/card-utilities';
import Validation from 'constants/validation-messages';
import Regex from 'constants/regex';
import * as yup from 'yup';
import Icon from 'assets/svg-icons';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import dayjs from 'dayjs';

const CardsFilter = ({ modalType, filterValues, onSubmit, resetFilterValues }) => {
  const allAccountsLoading = useSelector((state: any) => state.accountsStore.isAllAccountsLoading);
  const allAccounts = useSelector((state: any) => state.accountsStore.allAccounts);
  const [cardIssueDateFrom, setCardIssueDateFrom] = useState('');

  const isAccountNumberDisabled = !!modalType;

  const compareDates = (dateA, dateB) => {
    return !dateA || !dateB || yupDateTransformer(undefined, dateA) < yupDateTransformer(undefined, dateB);
  };

  const validationSchema = yup.object().shape({
    cfStartRange: yup
      .string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(16, Validation.MIN_MAX_CHAR_SIXTEEN)
      .max(16, Validation.MIN_MAX_CHAR_SIXTEEN)
      .matches(Regex.ONLY_NUMBERS, Validation.ONLY_NUMBERS),
    cfEndRange: yup
      .string()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(16, Validation.MIN_MAX_CHAR_SIXTEEN)
      .max(16, Validation.MIN_MAX_CHAR_SIXTEEN)
      .matches(Regex.ONLY_NUMBERS, Validation.ONLY_NUMBERS),
    cfAccountNumber: yup.string().nullable(),
    cfDriverName: yup
      .string()
      .matches(Regex.DRIVER_NAME, Validation.DRIVER_NAME)
      .max(27, Validation.MAX_CHAR_TWENTY_SEVEN),
    cfVehicleRegNumber: yup
      .string()
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .max(12, Validation.MAX_CHAR_TWELVE),
    cfDomesticNumber: yup
      .string()
      .matches(Regex.ALPHA_NUM_SPACE, Validation.ALPHA_NUM_SPACE)
      .max(16, Validation.MAX_CHAR_SIXTEEN),
    cfCostCentre: yup
      .string()
      .matches(Regex.ALPHA_NUM_SPACE_HYPHEN, Validation.ALPHA_NUM_SPACE_HYPHEN)
      .max(36, Validation.MAX_CHAR_THIRTY_SIX),
    cfIssueDateFrom: yup.string().nullable(),
    cfIssueDateTo: yup
      .string()
      .nullable()
      .test('isAfterIssueDateFrom', Validation.ISSUE_DATE_TO_AFTER_FROM, function (issueDateTo) {
        return compareDates(this.resolve(yup.ref('cfIssueDateFrom')), issueDateTo);
      }),
  });

  const filterFormOnChange = (data, name) => {
    if (name === 'cfIssueDateFrom') {
      setCardIssueDateFrom(data[name]);
    }
  };

  const getMinDates = () => {
    const frmDate = cardIssueDateFrom ? cardIssueDateFrom.split('/').reverse().join('/') : dayjs().format('YYYY/M/DD');
    const minDate = dayjs(frmDate).add(1, 'day');

    return minDate;
  };

  /**
   * Determines if a date should be disabled in the DatePicker
   *
   * @param {typeof dayjs} current date being evaludated
   */
  const disabledDate = (current) => {
    const minDate = getMinDates();

    return current < minDate;
  };

  return (
    <>
      <Form
        id={'cards-filters'}
        schema={validationSchema}
        defaultValues={filterValues}
        onSubmit={onSubmit}
        validationMode={FormValidationMode.onChange}
        onChange={filterFormOnChange}
      >
        <div className={`card-filters__grid ${modalType && 'card-filters__grid--modal'}`}>
          <div
            className={`card-filters__subtitle-0 card-filters__grid-col-span-all ${modalType && 'card-filters__grid-col-span-all--modal'}`}
          ></div>
          {isAccountNumberDisabled && (
            <FormField
              label={'Account number'}
              name={'cfAccountNumber'}
              forceFloating
              render={(onChange, onBlur, value) => (
                <ReadonlyField type='dropdown' key='cfAccountNumber' value={value} />
              )}
            />
          )}
          {!isAccountNumberDisabled && (
            <Form.DropdownSearch
              placeholder={'Please select'}
              type={'dropdown'}
              label={'Account number'}
              name={'cfAccountNumber'}
              loading={allAccountsLoading}
              filterOptions
              data={allAccounts?.map((item, index) => ({
                value: item.contractNumber,
                label: item.contractNumber + ' ' + item.contractName,
                level: item.level,
                parent: item.parent,
                index: index,
              }))}
            />
          )}
          <div
            className={`card-filters__subtitle-1 card-filters__grid-col-span-all ${modalType && 'card-filters__grid-col-span-all--modal'}`}
          >
            Card number (range)
          </div>
          <div className={`card-filters__grid-col-span-2 ${modalType && 'card-filters__grid-col-span-2--modal'}`}>
            <div className='card-filters__flex-item'>
              <Form.Input label={'Minimum'} name={'cfStartRange'} />
            </div>
            <div className={`card-filters__range-splitter ${modalType && 'card-filters__range-splitter--modal'}`} />
            <div className='card-filters__flex-item'>
              <Form.Input label={'Maximum'} name={'cfEndRange'} />
            </div>
          </div>
          <div
            className={`card-filters__subtitle-2 card-filters__grid-col-span-all ${modalType && 'card-filters__grid-col-span-all--modal'}`}
          >
            Issue date (range)
          </div>
          <div className={`card-filters__grid-col-span-2 ${modalType && 'card-filters__grid-col-span-2--modal'}`}>
            <div className={'card-filters__flex-item'}>
              <Form.DatePicker
                placeholder={'dd/mm/yyyy'}
                name={'cfIssueDateFrom'}
                label={'Issue date from'}
                labelType={'hidden'}
                inputSuffix={
                  <SuffixWrapper>
                    <Icon iconName='calender' />
                  </SuffixWrapper>
                }
                getPopupContainer={(trigger) => (modalType ? document.body : trigger.parentElement)}
              />
            </div>
            <div className={`card-filters__range-splitter ${modalType && 'card-filters__range-splitter--modal'}`} />
            <div className={'card-filters__flex-item'}>
              <Form.DatePicker
                placeholder={'dd/mm/yyyy'}
                name={'cfIssueDateTo'}
                label={'Issue date to'}
                labelType={'hidden'}
                inputSuffix={
                  <SuffixWrapper>
                    <Icon iconName='calender' />
                  </SuffixWrapper>
                }
                getPopupContainer={(trigger) => (modalType ? document.body : trigger.parentElement)}
                disabledDate={disabledDate}
              />
            </div>
          </div>
          <Form.DropdownSearch
            label={'Card status'}
            type={'dropdown'}
            name={'cfStatus'}
            placeholder={'Card status'}
            data={cardStatusFilterOptions}
            allowClear={false}
          />
          <Form.Input label={'Driver name'} name={'cfDriverName'} className={'card-filters__uppercase'} />
          <Form.Input
            label={'Vehicle registration'}
            name={'cfVehicleRegNumber'}
            className={'card-filters__uppercase'}
          />
          <Form.Input label={'Domestic number'} name={'cfDomesticNumber'} className={'card-filters__uppercase'} />
          <Form.Input label={'Cost centre'} name={'cfCostCentre'} className={'card-filters__uppercase'} />
          <div className='filter-confirmation'>
            <AmpolButton
              variant={ButtonVariant.Secondary}
              color={ButtonColor.Blue}
              label='Clear All'
              type={ButtonType.Reset}
              onClick={resetFilterValues}
            />
            <AmpolButton
              variant={ButtonVariant.Primary}
              color={ButtonColor.Blue}
              label='Filter'
              type={ButtonType.Submit}
              formId={'cards-filters'}
            />
          </div>
        </div>
      </Form>
    </>
  );
};

export default CardsFilter;
